import { HUMAN_STATUSES, MAIN_TAB, hasNoFile } from '@constants'
import * as store from '@store'
import { useCallback, useMemo, useRef } from 'react'
import { useWireState, useWireValue } from '@forminator/react-wire'
import useDocumentPopupHandler from '@hook/useDocumentPopupHandler'
import { useSearchParams } from 'react-router-dom'
import { standardizeParties } from '@/components/shared/utils'

const placeholders = {
    grantors: { deed: 'Add Grantor(s)', lease: 'Add Lessor(s)' },
    grantees: { deed: 'Add Grantee(s)', lease: 'Add Lessee(s)' },
    fullGrantor: { deed: 'Add Full Grantor', lease: 'Add Full Lessor' },
    fullGrantee: { deed: 'Add Full Grantee', lease: 'Add Full Lessee' },
}

const fieldKeys = [
    'document.name',
    'grantors',
    'grantees',
    'fullGrantor',
    'fullGrantee',
    'bookVolume',
    'bookPage',
    'bookType',
    'instrumentNumber',
    'instrumentType',
    'instrumentDate',
    'fileDate',
    'legalDescriptions',
    'comment',
    'recordingDate',
]

const RunsheetDocumentsRowViewModel = (rsDocJoin, value) => {
    
    const trRef = useRef()
    
    const [searchParams] = useSearchParams()
    
    const runsheetHeaders = useWireValue(store.runsheetHeaders)
    const [,setRunsheetDocumentValue] = useWireState(store.runsheetDocumentValue)
    const runsheet = useWireValue(store.currentRunsheet)
    const tabs = useWireValue(store.runsheetTabs)
    const runsheetTriedChaining = useWireValue(store.runsheetTriedChaining)
    
    const activeTab = useMemo(() => searchParams.get('activeTab') || MAIN_TAB, [searchParams])
    const currentTab = useMemo(() => tabs.find(t => t.id === activeTab) || tabs[0], [tabs, activeTab])
    
    const document = useMemo(() => rsDocJoin.document, [rsDocJoin])
    
    const failedOnChaining = useMemo(() => {
        const runsheetOrTabId = activeTab === MAIN_TAB ? runsheet.id : activeTab
        
        return runsheetTriedChaining[runsheetOrTabId] || false
    }, [runsheetTriedChaining, activeTab, runsheet.id])
    
    const missingDataForChain = useMemo(() =>
        !value.instrumentDate || !value.grantors.length || !value.grantees.length, [value])
    
    const fieldsToShow = useMemo(() => (
        runsheetHeaders.length
            ? runsheetHeaders
            : fieldKeys
    ), [runsheetHeaders])
    
    const getTooltipData = useCallback(document => {
        return document ? {
            documentName: document.name,
            documentType: document.documentType,
            collectionName: document.collection?.name,
            status: HUMAN_STATUSES[document.orgStatus] || HUMAN_STATUSES[document.reviewStatus],
            hasFile: !hasNoFile(document),
        } : {}
    }, [])
    
    const applyCasing = (text = '') => {
        if (!text) return
        
        switch (runsheet?.partyCasing) {
            case 'none':
                return text
            case 'upper':
                return text.toUpperCase()
            case 'proper':
                return standardizeParties(text)
        }
        
        return text
    }
    
    const { popupDocumentOrNavigate } = useDocumentPopupHandler()
    
    return {
        
        // Constants
        placeholders,
        
        // Refs
        trRef,
        
        // State
        document,
        
        // Global State
        runsheetHeaders,
        setRunsheetDocumentValue,
        runsheet,
        tabs,
        runsheetTriedChaining,
        
        // Memos
        activeTab,
        currentTab,
        failedOnChaining,
        missingDataForChain,
        fieldsToShow,
        
        // Hooks
        searchParams,
        popupDocumentOrNavigate,
        
        // Methods
        getTooltipData,
        applyCasing,
        
    }
    
}

export default RunsheetDocumentsRowViewModel
